<template>
  <DialogHeader class="items-center">
    <DialogTitle>Receive Direct Payment</DialogTitle>
  </DialogHeader>

  <div class="h-[1px] w-full my-2 bg-grey-light-200" />

  <p v-if="owner" class="text-grey-light-700">{{ owner }}</p>
  <div class="flex justify-between text-grey-light-700">
    <span>Order Reference</span>
    <span class="text-grey-light-900">{{ order?.reference }}</span>
  </div>
  <div class="flex justify-between text-grey-light-700">
    <span>Balance</span>
    <span class="text-grey-light-900">{{ displayCurrency(order?.balance as number) }}</span>
  </div>

  <div class="h-[1px] w-full my-2 bg-grey-light-200"></div>

  <CurrencyInput
    v-model="amount"
    :disabled="isLoading"
    :label="`Amount Paid`"
    class="text-left"
    v-model:is-valid="isAmountValid"
    :warning-max="order?.balance"
  />

  <TextInput
    id="paymentReference"
    label="Payment Reference"
    placeholder="Enter Ref"
    test-id="payment_reference"
    :disabled="isLoading"
    v-model:input="paymentReference"
    v-model:is-input-valid="isPaymentReferenceValid"
  />

  <Calendar :show="true" v-model:date="date" :label="'Verified Date'" />

  <DialogFooter>
    <Button @click="receivePayment" :disabled="!canCreatePayment">Receive Payment</Button>
  </DialogFooter>
  <div v-if="error !== ''" class="flex justify-center">
    <span class="text-sm font-medium text-error-500">
      {{ error }}
    </span>
  </div>
</template>

<script setup lang="ts">
import Calendar from '@/components/common/CalendarSelect.vue';
import CurrencyInput from '@/components/common/CurrencyInput.vue';
import TextInput from '@/components/common/TextInput.vue';
import { Button } from '@/components/ui/button';
import { DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { getOrderByIdWithPlansPaymentsAndConnectors } from '@/services/orders';
import { createDirectPayment } from '@/services/payments';
import { useOrderStore } from '@/stores/orders';
import { useUserStore } from '@/stores/users';
import { displayCurrency } from '@/utils/format.utils';
import { addMetric, createException } from '@/utils/monitoring.utils';
import { computed, ref } from 'vue';
import type { ModelOrderResponse } from '@layup/api-sdk';

const props = defineProps<{
  order?: Partial<ModelOrderResponse>;
}>();

const emit = defineEmits(['record', 'update:date']);

const userStore = useUserStore();
const orderStore = useOrderStore();

const amount = ref(0);
const paymentReference = ref('');
const isLoading = ref(false);
const owner = ref(props.order?.plans?.[0]?.benefactor?.name);
const date = ref(new Date());
const isAmountValid = ref(false);
const isPaymentReferenceValid = ref(false);
const canCreatePayment = computed(() => {
  return isAmountValid.value && isPaymentReferenceValid.value;
});
const error = ref<string>('');

const receivePayment = async () => {
  error.value = '';
  isLoading.value = true;

  try {
    const response = await createDirectPayment({
      amount: amount.value,
      paymentPlanId: props.order?.plans?.[0]?._id,
      reference: paymentReference.value,
      note: `Created by ${userStore.user?._id} via the merchant dashboard.`,
      verifiedAt: new Date(date.value).toISOString(),
    });
    if (response.paid == true) {
      emit('record');
      emit('update:date', date.value);
      amount.value = 0;
      paymentReference.value = '';
      isAmountValid.value = false;
      isPaymentReferenceValid.value = false;
      date.value = new Date();
      addMetric('direct-payment', {
        success: true,
        value: amount.value,
      });
      // Reload order with new payment into the store.
      const refreshedOrder = await getOrderByIdWithPlansPaymentsAndConnectors(props.order?._id as string);
      orderStore.updateOrder(refreshedOrder);
    } else {
      error.value = 'Payment could not be processed. Please contact support.';
      addMetric('direct-payment', {
        success: false,
        value: amount.value,
      });
    }
  } catch (err) {
    createException(err);
    addMetric('direct-payment', {
      success: false,
      value: amount.value,
      error: (err as Error).message,
    });
    error.value = 'An error occurred while processing the payment. Please contact support.';
  }
  isLoading.value = false;
};
</script>
